import { useEffect, useState, type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { debounce } from 'lodash'
import { SearchElement, Dropdown, Avatar, type IDropdownProps } from 'shared/ui'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type SearchTypesEnum } from 'widgets/SearchDropdown'
import { UsersDropdownContent } from 'widgets/SearchDropdown/ui/DropdownContent/UsersDropdownContent/UsersDropdownContent'
import { type SearchDropdownUsersStore } from 'widgets/SearchDropdown/store/searchDropdownUsersStore'
import styles from './styles.module.scss'

type UserElementProps = {
  parentsDropdownId?: string[]
  store: SearchDropdownUsersStore
  onRemove: (type: SearchTypesEnum) => void
  activeType: SearchTypesEnum | null
  canShowChildDropdown: boolean
  dropdownProps?: Partial<IDropdownProps>
}

export const UserElement: FC<UserElementProps> = observer(
  ({ parentsDropdownId, store, onRemove, activeType, canShowChildDropdown, dropdownProps }) => {
    const { usersStore } = useEntitiesContext()

    const {
      hasSelectedItems,
      selectedItemsCount,
      firstSelectedItemId,
      selectedItemsIds,
      title,
      pluralTitle,
      type,
      disableClosing,
    } = store
    const [show, setShow] = useState(false)

    const onShow = () => {
      const showDropdown = activeType === type && canShowChildDropdown

      setShow(showDropdown)
    }

    const showDebounce = debounce(onShow, 300)

    useEffect(() => {
      showDebounce()
    }, [canShowChildDropdown, activeType])

    const Label = () => {
      const firstItem = usersStore.getItem(firstSelectedItemId)

      if (hasSelectedItems && selectedItemsCount === 1 && firstItem) {
        return (
          <div className={styles.container}>
            <div className={styles.avatar}>
              <Avatar size={16} info={firstItem?.avatarInfo} />
            </div>
            <span className={styles.selectedLabel}>{firstItem.name}</span>
          </div>
        )
      }

      return (
        <div className={styles.container}>
          <div
            className={styles.avatars}
            style={{ width: (selectedItemsCount < 3 ? selectedItemsCount : 3) * 12 }}
          >
            {selectedItemsIds.slice(0, 3).map((item, idx) => {
              const member = usersStore.getItem(item)
              if (!member) return
              return (
                <div className={styles.avatar} key={idx} style={{ left: idx * 12, zIndex: idx }}>
                  <Avatar size={16} info={member.avatarInfo} />
                </div>
              )
            })}
          </div>
          <span className={styles.selectedLabel}>
            {selectedItemsCount} {pluralTitle}
          </span>
        </div>
      )
    }

    return (
      <Dropdown
        ariaLabel={title}
        parentsDropdownId={parentsDropdownId}
        iconButtonProps={{
          size: 'medium',
          fontSize: 20,
          ariaLabel: `${title}_trigger`,
        }}
        show={show}
        triggerComponent={(open) => (
          <SearchElement
            icon={'userFilled'}
            type={title}
            contentDefaultValue='select'
            warningContent={!open && !hasSelectedItems}
            removeCallback={() => {
              onRemove(type)
            }}
            isViewOnly={disableClosing}
            content={hasSelectedItems && <Label />}
          />
        )}
        placement={'bottom-start'}
        sizeTriggerButton={'medium'}
        variantTriggerButton={'icon'}
        customComponent={() => <UsersDropdownContent store={store} />}
        {...dropdownProps}
      />
    )
  }
)
